import { useId, type ReactNode } from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cn } from '@/lib/utils'

/**
 * Field — обёртка поля: подпись (label) + подсказка (hint) + сообщение об ошибке вокруг контрола
 * (`.field` макета). Подпись программно связана с полем; ошибка объявляется AT (role=alert).
 * Контрол получает id/aria-* через render-проп `children`.
 */
export interface FieldRenderArgs {
  id: string
  'aria-invalid': boolean | undefined
  'aria-describedby': string | undefined
  /**
   * Обязательность для вспомогательных технологий. Маркер «*» у подписи от них скрыт
   * (`aria-hidden`), поэтому состояние несёт сам контрол. Именно `aria-required`, а НЕ нативный
   * `required`: последний включил бы браузерную валидацию формы и изменил её поведение, тогда как
   * проверки в этом кабинете свои (формы отправляются с `noValidate`).
   */
  'aria-required': true | undefined
}

export interface FieldProps {
  label: string
  hint?: string
  error?: string
  invalid?: boolean
  /**
   * Обязательное поле: маркер «*» после текста подписи (centrika-docs, `common.required-fields`)
   * и `aria-required` у контрола через `FieldRenderArgs`.
   */
  required?: boolean
  className?: string
  children: (args: FieldRenderArgs) => ReactNode
}

/**
 * Маркер обязательного поля — «*» после текста подписи (centrika-docs, `common.required-fields`).
 * Отдельный компонент, потому что несколько полей рисуют подпись мимо `Field` (файловый ввод,
 * многострочный текст, поле внутри выпадающего списка) — маркер там обязан выглядеть так же.
 */
export function RequiredMark() {
  return (
    <span className="text-red" aria-hidden="true">
      {' '}
      *
    </span>
  )
}

export function Field({ label, hint, error, invalid, required, className, children }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const isInvalid = invalid ?? Boolean(error)
  // hint рендерится только когда нет error → не ссылаемся на несуществующий узел в aria-describedby
  const describedBy =
    [error ? errorId : null, hint && !error ? hintId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('my-4', className)}>
      <LabelPrimitive.Root htmlFor={id} className="mb-1.5 block text-13 text-txt-secondary">
        {label}
        {required ? <RequiredMark /> : null}
      </LabelPrimitive.Root>
      {children({
        id,
        'aria-invalid': isInvalid || undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {hint && !error ? (
        <p id={hintId} className="mt-1.5 text-12 text-txt-tertiary">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-12 text-red">
          {error}
        </p>
      ) : null}
    </div>
  )
}
